import React from 'react';
import { connect } from 'react-redux';
import { first, last } from 'lodash';
import moment from 'moment';
import { extendMoment } from 'moment-range';

import { feSidebarOpen } from 'actions/ui.actions';
import { determineIfAppointmentMoveBlocked } from 'bl/scheduler/determineIfAppointmentMoveBlocked.bl';
import determineIfDisabledDrag from 'bl/scheduler/determineIfDisabledDrag.bl';
import determineIfDragDisablingFetch from 'bl/scheduler/determineIfDragDisablingFetch.bl';
import { determineIfTimeOffMoveBlocked } from 'bl/scheduler/determineIfTimeOffMoveBlocked.bl';
import shouldUpdateWeekColumn from 'bl/scheduler/shouldUpdateWeekColumn.bl';
import timeInRangeOld from 'bl/scheduler/timeInRangeOld.bl';
import DndDraggable from 'components/dnd/DndDraggable.component';
import FLAG from 'config/flag.singleton';
import SP from 'constants/ui/sidePanel.const';
import { withTranslation } from 'providers/GILT.provider';
import { selectBooking } from 'selectors/booking.selectors';
import { selectOldFlagsHideMiniModal } from 'selectors/oldFlags.selectors';
import { selectRequestStatus } from 'selectors/request.selectors';
import { selectSidebarOpen, selectSidebarPanel, selectSidebarProcedureModalMounted } from 'selectors/sidebar.selectors';
import { selectTimeOffFormCategory, selectTimeOffNonce } from 'selectors/timeoff.selectors';
import { firstOf, lastOf } from 'utils/arrays/elements.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { formatLocalizedTime, formatYearToDayCompact } from 'utils/date/format.util';
import { nowAsMoment, nowAsTimestamp } from 'utils/date/now.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import diffShallow from 'utils/objects/diffShallow.util';
import equalProps from 'utils/objects/equalProps.util';
import parseJson from 'utils/parsers/parseJson';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import { isEqualByString } from 'utils/predicates/string/simple.util';
import { isTimeEqual } from 'utils/predicates/time/compare.util';
import dnd from 'utils/react/dnd.util';
import setStateAfterRender from 'utils/react/setStateAfterRender.util';
import randomString from 'utils/values/randomString.util';
import uniqueIndexGenerator from 'utils/values/uniqueIndexGenerator.util';

import { setType } from 'booking/actions';
import { displayPermissionToast } from 'notification/actions';
import { createIfAllowed, setBookedByButton } from 'scheduler/actions';
import NewEntryPlaceholder from 'scheduler/components/NewEntryPlaceholder';
import ScheduleEvent from 'scheduler/components/ScheduleEvent';
import { ScheduleProcedure } from 'scheduler/components/ScheduleProcedure';
import SchedulerCell from 'scheduler/components/SchedulerCell.component';
import { calculateOverlapping, isInWorkingRange, rangeHoursNoChange } from 'scheduler/utils';

const L = logger('WeekColumn');

const STEP = 15;

const extractWeekRange = (options) => {
  const hours = options?.hours;
  const date = options?.date;

  const bgn = firstOf(hours);
  const end = lastOf(hours);

  const from = moment(date).set({ hour: bgn.get('hour'), minute: bgn.get('minute'), second: 0, millisecond: 0 });

  const minute = end.get('minute');
  const hour = end.get('hour');

  const to = moment(bgn).isSame(moment(end), 'day')
    ? moment(date).set({ hour, minute, second: 0, millisecond: 0 })
    : moment(moment(date).add(1, 'days')).set({ hour, minute, second: 0, millisecond: 0 });

  const range = extendMoment(moment).range(moment(from), moment(to)).by('minutes', { step: STEP });

  return Array.from(range);
};

class WeekColumn extends React.Component {
  static defaultProps = { bookingTime: null };

  static getDerivedStateFromProps(nextProps, state) {
    const currentRangeByHours = extractWeekRange({ hours: nextProps.rangeByHours, date: nextProps.dayWholeDate });
    return state.currentRangeByHours && !rangeHoursNoChange(state.currentRangeByHours, currentRangeByHours) ? { currentRangeByHours } : null;
  }

  constructor(props) {
    super(props);
    const { dayWholeDate, rangeByHours } = this.props;
    this.state = {
      currentRangeByHours: extractWeekRange({ hours: rangeByHours, date: dayWholeDate }),
      draggingOverTime: '',
      dragOverTimeEl: null,
    };
  }

  shouldComponentUpdate(nextProps) {
    return shouldUpdateWeekColumn({ next: nextProps, state: this.state, prev: this.props });
  }

  getStyle = (style, snapshot, procedureId) => {
    const draggingOverProps = parseJson(snapshot.draggingOver);
    const elDragOverTime = document.querySelector('#dragOverTime');
    let draggingOverTime = '';

    if (draggingOverProps) draggingOverTime = formatLocalizedTime(draggingOverProps.time);

    if (procedureId) {
      let dragEl;
      if (typeof procedureId === 'number') {
        dragEl = document.querySelector(`.is-${procedureId} .cal-container`);
      } else if (procedureId) {
        [dragEl] = Array.prototype.slice.call(document.querySelectorAll(`.${procedureId}`)).filter((e) => e.parentElement.style.transform);
      }
      if (elDragOverTime && draggingOverTime !== this.state.draggingOverTime && style.transform) {
        elDragOverTime.innerText = draggingOverTime;
        setStateAfterRender(this, { draggingOverTime });
      } else if (elDragOverTime && !style.transform) {
        elDragOverTime.remove();
        setStateAfterRender(this, { draggingOverTime });
      }

      const elDiv = document.createElement('div');
      elDiv.setAttribute('id', 'dragOverTime');
      const timeEl = document.createTextNode(draggingOverTime);
      elDiv.appendChild(timeEl);
      if (dragEl && !elDragOverTime && style.transform) {
        dragEl.style.display = 'flex';
        dragEl.prepend(elDiv);
        setStateAfterRender(this, { draggingOverTime, dragOverTimeEl: elDiv });
      }
    }

    const elAlreadyDraggedAlert = document.querySelector('.move-notification');
    if (elAlreadyDraggedAlert) return style;
    const newStyle = Object.assign({}, style);

    if (!snapshot.isDropAnimating) return newStyle;
    return { ...newStyle, transitionDuration: '0.001s' };
  };

  createBooking = (time) => {
    const { appointmentPerms, createIfAllowed, stylist, location } = this.props;
    const { pathname } = location;

    if (appointmentPerms?.isCreatePersonalOnly && !isEqualByString(stylist?.id, appointmentPerms?.accessStylistId)) {
      this.showCreatePermissionToast('ownCol');
      return;
    }

    L.debug('createBooking()', { time, stylist, pathname });

    const hasSlash = pathname.substr(pathname.length - 1) === '/';
    const path = `${pathname}${hasSlash ? '' : '/'}appointment/new`;

    createIfAllowed(stylist?.id, time, path, 'appointment');
  };

  createNewTimeoff = (time) => {
    const { salonId, stylist, setType, feSidebarOpen } = this.props;

    L.debug('createNewTimeoff()', { time, salonId, stylist });

    setType('timeoff');

    feSidebarOpen({
      dateString: formatYearToDayCompact(nowAsTimestamp()),
      panel: SP.timeOff,
      salonId,
      stylist,
      stylistId: stylist?.id,
      time,
    });
  };

  createTimeoff = (time) => {
    const { createIfAllowed, setType, stylist, location } = this.props;
    const { pathname } = location;

    L.debug('createTimeoff()', { time, stylist, pathname });

    const path = `${pathname}/appointment/new`;
    createIfAllowed(stylist?.id, time, path, 'timeoff');
    setType('timeoff');
  };

  showCreatePermissionToast = (key) => {
    const { displayPermissionToast, t } = this.props;
    const text = key === 'noCreate' ? t('scheduler.appointmentPermissions.createNoAccessTooltip') : t('scheduler.appointmentPermissions.ownColumnCreateOnly');
    displayPermissionToast(text, `ap-perm-${key}`);
  };

  handleSchedulerCellClick = ({ currentEvents, currentProcedures, currentTime }) => {
    const { appointmentPerms, createIfAllowed, hideMiniModal, location, setBookedByButton, stylistId, timeOffPerms } = this.props;

    if (isFullArray(currentProcedures) || isFullArray(currentEvents)) return;

    const { accessStylistId: permStylistId, isCreateBlocked, isCreatePersonalOnly } = appointmentPerms ?? {};
    const timeOffPermStylistId = timeOffPerms?.accessStylistId;
    const timeOffPersonalColBlocked = timeOffPerms?.isCreatePersonalOnly && !isEqualByString(stylistId, timeOffPermStylistId);
    const timeOffDeniedForSlot = timeOffPerms?.isCreateBlocked || timeOffPersonalColBlocked;
    const apptPersonalColBlocked = isCreatePersonalOnly && !isEqualByString(stylistId, permStylistId);
    const apptDeniedForSlot = isCreateBlocked || apptPersonalColBlocked;

    if (apptDeniedForSlot && timeOffDeniedForSlot) {
      this.showCreatePermissionToast(apptPersonalColBlocked || timeOffPersonalColBlocked ? 'ownCol' : 'noCreate');
      return;
    }

    createIfAllowed(stylistId, currentTime, hideMiniModal ? `${location.pathname}/appointment/new/` : location.pathname);
    setBookedByButton(false);
  };

  render() {
    const { currentRangeByHours, dragOverTimeEl } = this.state;

    const {
      addedServices,
      booking,
      bookingTime,
      categoriesAndServices,
      currentActiveProcedure,
      events,
      filteredNoNewlyAddedProcedures,
      history,
      isDragging,
      markProcedures,
      newEntryIndicator,
      onCheckout,
      onDeleteEvent,
      openingHours,
      panel,
      isSidebarOpen,
      pickCurrentStylist,
      salonId,
      salonTimezone,
      showModal,
      stylist,
      stylistId,
      stylists,
      timeOffCategory,
      timeOffNonce,
      updateProcedure,
      updateProcedureStatus,
      isDragDisabledByFetch,
      isProcedureModalMounted,
      appointmentPerms,
      timeOffPerms,
    } = this.props;

    const isMoveBlocked = appointmentPerms?.isMoveBlocked;
    const isMovePersonalOnly = appointmentPerms?.isMovePersonalOnly;
    const permStylistId = appointmentPerms?.accessStylistId;
    const canEditDuration = appointmentPerms?.canEditDuration;
    const canCreateAllStaff = appointmentPerms?.canCreateAll;

    const {
      type: bookType,
      startTime: bookBgn,
      show_stylist: isBookStylistShown,
      endTime: bookEnd,
      repeat_rule: bookRepeatRule,
      allDay: isBookAllDay,
      currentTime: bookTime,
      currentStylistId,
    } = booking ?? {};

    const isTimeOffType = bookType === 'timeoff';
    const bookRecurrences = bookRepeatRule === 'once' ? '' : 'RRULE:FREQ=NONE';

    const draggableIndex = uniqueIndexGenerator(0);
    const now = nowAsMoment();
    const firstHour = first(currentRangeByHours);
    const lastHour = last(currentRangeByHours);

    const allDayEvents = events
      .filter((event) => moment(event.start_time).tz(salonTimezone).isBefore(firstHour) && moment(event.end_time).tz(salonTimezone).isAfter(firstHour))
      .map((event) => ({
        event: {
          ...event,
          end_time: moment(event.end_time).tz(salonTimezone).isAfter(lastHour) ? lastHour.toISOString() : moment(event.end_time),
        },
        key: `event-${event?.id || randomString.long()}`,
        currentTime: firstHour,
        salonTimezone,
        salonId,
        onDeleteEvent,
      }));

    const allowModal = true;

    const stackOrder = {};
    const currentBookingProcedures = addedServices.filter((service) => service.deleted !== true).map((service) => ({ ...service, dismissModal: true, editing: true }));

    const checkedProcedures = calculateOverlapping([...filteredNoNewlyAddedProcedures, ...currentBookingProcedures]);

    const isDragDisabledEditLess = isDragDisabledByFetch || determineIfDisabledDrag({ panel, modal: isProcedureModalMounted });
    const isTimeOffMoveBlocked = (eventStylistId) => determineIfTimeOffMoveBlocked({ timeOffPerms, eventStylistId });

    return (
      <div data-bem="WeekColumn">
        <div data-bem="WeekColumn__on-move" className="on-move">
          <div data-bem="WeekColumn__cell-column" className="cell-column">
            {allDayEvents.map((props) => (
              <ScheduleEvent tag="WeekColumn--01" openingHours={openingHours} firstTime={firstHour} lastTime={lastHour} {...props} allowModal={allowModal} dayView={false} />
            ))}

            {currentRangeByHours.map((currentTime) => {
              const isCurrentTimeInRange = timeInRangeOld(now, currentTime, salonTimezone, 15);
              const isBookTimeInRange = timeInRangeOld(bookingTime, currentTime, salonTimezone, 15);
              const isNewEnabled = fromAnyToBool(newEntryIndicator && bookingTime && isBookTimeInRange && !isTimeOffType);

              const isNewTimeoffBlock = fromAnyToBool(!isBookAllDay && isTimeOffType && isTimeEqual(bookBgn, currentTime) && currentStylistId === stylistId);

              const currentProcedures = checkedProcedures
                .filter(({ procedure }) => timeInRangeOld(procedure.start_time, currentTime, salonTimezone, 15))
                .map(({ overlapStatus, procedure }) => {
                  const { start_time: start, id, duration } = procedure;

                  const index = moment(currentTime);
                  while (index.isBefore(moment(start).add(moment.duration(duration)))) {
                    stackOrder[index] = stackOrder[index] ? stackOrder[index] + 1 : 1;
                    index.add(15, 'minutes');
                  }

                  return {
                    procedure,
                    overlapStatus,
                    key: `procedure-${id}`,
                    currentTime,
                    currentActiveProcedure,
                    stackOrder: stackOrder[currentTime],
                    salonId,
                    history,
                    dayView: false,
                    onCheckout,
                    updateProcedureStatus,
                    updateProcedure,
                    markLinkedProcedures: markProcedures,
                  };
                });

              const currentEvents = events
                .filter((event) => moment(event.start_time).isSame(currentTime))
                .filter((event) => timeInRangeOld(event.start_time, currentTime, salonTimezone, 15))
                .map((event) => ({
                  event,
                  key: `event-${event?.id || randomString.long()}`,
                  salonTimezone,
                  currentTime,
                  salonId,
                  history,
                  onDeleteEvent,
                }));

              return (
                <SchedulerCell
                  stylist={stylist}
                  droppable
                  time={currentTime}
                  isDragging={isDragging}
                  key={`cell-${currentTime.format('HH:mm')}-${stylist?.id}`}
                  isInWorkingHour={isInWorkingRange(currentTime, openingHours)}
                  hovered={false}
                  onClick={() =>
                    this.handleSchedulerCellClick({
                      currentEvents,
                      currentProcedures,
                      currentTime,
                    })
                  }
                  dragOverTimeEl={dragOverTimeEl}
                >
                  <div data-bem="WeekColumn__schedule-cell" className={`${isCurrentTimeInRange ? 'current_time' : ''} current-entries`} style={{ display: 'flex', width: '100%' }}>
                    {isNewTimeoffBlock && (
                      <DndDraggable
                        tag="WeekColumn__event--single--dnd-source"
                        draggableId={dnd.dragEvent({
                          event: {
                            client_id: null,
                            end_time: bookEnd,
                            feedback: null,
                            id: -6000,
                            local_id: 10,
                            name: '',
                            notes: '',
                            origin: 'salon_created',
                            price: 0,
                            recurrences: 'RRULE:FREQ=NONE',
                            start_time: bookBgn,
                          },
                        })}
                        key={bookTime}
                        index={draggableIndex()}
                        isDragDisabled={isDragDisabledEditLess}
                      >
                        {({ dragHandleProps, draggableProps, innerRef }, snapshot) => {
                          const isTimeOffMovePermissionBlocked = isTimeOffMoveBlocked(stylistId);

                          return (
                            <div
                              data-bem="WeekColumn__event--single--el-source"
                              data-testid={isTimeOffMovePermissionBlocked ? 'ScheduleEvent__move-blocked--draft' : 'ScheduleEvent__move--draft'}
                              data-move-blocked={isTimeOffMovePermissionBlocked ? 'true' : undefined}
                              ref={innerRef}
                              {...draggableProps}
                              {...dragHandleProps}
                              style={{
                                ...this.getStyle(draggableProps.style, snapshot),
                                ...(isTimeOffMovePermissionBlocked ? { cursor: 'not-allowed' } : {}),
                              }}
                            >
                              <ScheduleEvent
                                tag="WeekColumn--03--single"
                                firstTime={bookBgn}
                                lastTime={bookEnd}
                                openingHours={openingHours}
                                isBeingDragged={snapshot.isDragging}
                                event={{
                                  client_id: 0,
                                  end_time: bookEnd,
                                  feedback: null,
                                  id: -6000,
                                  local_id: 10,
                                  name: '',
                                  notes: '',
                                  origin: 'salon_created',
                                  price: 0,
                                  recurrences: bookRecurrences,
                                  start_time: bookBgn,
                                  salon_id: salonId,
                                  status: 'time_off',
                                  stylist_id: stylistId,
                                  show_stylist: isBookStylistShown,
                                  time_off_category: timeOffCategory,
                                }}
                                onDeleteEvent={alwaysNoop}
                                allowModal={allowModal}
                                salonId={salonId}
                                nonce={timeOffNonce}
                              />
                            </div>
                          );
                        }}
                      </DndDraggable>
                    )}

                    {currentEvents.map((props, index) => {
                      const isTimeOffMovePermissionBlocked = isTimeOffMoveBlocked(props.event?.stylist_id);

                      return (
                        <DndDraggable tag="WeekColumn__event--dnd-source" draggableId={dnd.dragEvent({ event: props.event })} key={props.key} index={index} isDragDisabled={isDragDisabledEditLess}>
                          {({ dragHandleProps, draggableProps, innerRef }, snapshot) => (
                            <div
                              data-bem="WeekColumn__event--el-source"
                              data-testid={isTimeOffMovePermissionBlocked ? `ScheduleEvent__move-blocked-${props.event?.id}` : `ScheduleEvent__move-${props.event?.id}`}
                              data-move-blocked={isTimeOffMovePermissionBlocked ? 'true' : undefined}
                              ref={innerRef}
                              {...draggableProps}
                              {...dragHandleProps}
                              style={{
                                ...this.getStyle(draggableProps.style, snapshot, `event${props.event?.id}`),
                                ...(isTimeOffMovePermissionBlocked ? { cursor: 'not-allowed' } : {}),
                              }}
                            >
                              <ScheduleEvent
                                {...props}
                                tag="WeekColumn--02"
                                firstTime={firstHour}
                                lastTime={lastHour}
                                openingHours={openingHours}
                                isBeingDragged={snapshot.isDragging}
                                allowModal={allowModal}
                                dayView={false}
                                classKey={`event${props.event?.id ? props.event?.id : ''}`}
                              />
                            </div>
                          )}
                        </DndDraggable>
                      );
                    })}

                    {currentProcedures.map((localProps, index) => {
                      const { procedure, key } = localProps ?? {};
                      const { dismissModal, editing, id: procedureRowId } = procedure ?? {};
                      const procedureStylistId = fromAnyToString(procedure?.stylist?.id || procedure?.stylist_id);
                      const isForeignStylistDetailsHidden =
                        appointmentPerms?.isPersonalCalendarOnly && procedureStylistId && fromAnyToString(permStylistId) && procedureStylistId !== fromAnyToString(permStylistId);

                      const isDragDisabledByPermission = isMovePersonalOnly && !canCreateAllStaff && !isEqualByString(stylistId, permStylistId);
                      const isAppointmentMovePermissionBlocked = determineIfAppointmentMoveBlocked({ appointmentPerms, procedureStylistId });
                      // Intentionally not in isDragDisabledEditFull: drag must start so onDragStart toast + onDragEnd revert work.

                      const isDragDisabledEditFull =
                        isDragDisabledByPermission ||
                        isSidebarOpen ||
                        isDragDisabledEditLess ||
                        determineIfDisabledDrag({
                          panel,
                          editing,
                          modal: isProcedureModalMounted,
                        });

                      const isPermBlockedCursor = isMoveBlocked || isDragDisabledByPermission || isAppointmentMovePermissionBlocked;

                      return (
                        <DndDraggable tag="WeekColumn__procedure--dnd-source" draggableId={dnd.dragProcedure({ procedure })} isDragDisabled={isDragDisabledEditFull} key={key} index={index}>
                          {({ dragHandleProps, draggableProps, innerRef }, snapshot) => (
                            <div
                              data-bem="WeekColumn__procedure--el-source"
                              ref={innerRef}
                              {...draggableProps}
                              {...dragHandleProps}
                              style={{ ...this.getStyle(draggableProps.style, snapshot, procedureRowId), ...(isPermBlockedCursor ? { cursor: 'not-allowed' } : {}) }}
                            >
                              <ScheduleProcedure
                                {...localProps}
                                isBeingDragged={snapshot.isDragging}
                                markLinkedProcedures={markProcedures}
                                categoriesAndServices={categoriesAndServices}
                                stylists={stylists}
                                allowModal={allowModal && !dismissModal}
                                dayView={false}
                                showModal={showModal}
                                currentActiveProcedure={currentActiveProcedure}
                                isDragDisabledIndicator={isDragDisabledEditLess}
                                isForeignStylistDetailsHidden={isForeignStylistDetailsHidden}
                                canEditDuration={canEditDuration}
                              />
                            </div>
                          )}
                        </DndDraggable>
                      );
                    })}

                    {isNewEnabled && (
                      <DndDraggable tag="WeekColumn__handle--dnd-source" draggableId={dnd.dragPlaceholder()} key="placeholder" index={0} isDragDisabled>
                        {({ dragHandleProps, draggableProps, innerRef }) => (
                          <div data-bem="WeekColumn__handle--el-source" ref={innerRef} {...draggableProps} {...dragHandleProps}>
                            <NewEntryPlaceholder
                              columnStylistId={stylistId}
                              createBooking={this.createBooking}
                              createNewTimeoff={this.createNewTimeoff}
                              createTimeoff={this.createTimeoff}
                              schedulerTime={currentTime}
                              miniModal
                              pickCurrentStylist={pickCurrentStylist}
                            />
                          </div>
                        )}
                      </DndDraggable>
                    )}
                  </div>
                </SchedulerCell>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
}

const WeekColumnHoc = connect(
  (state) => ({
    booking: selectBooking(state),
    hideMiniModal: selectOldFlagsHideMiniModal(state),
    panel: selectSidebarPanel(state),
    isSidebarOpen: selectSidebarOpen(state),
    isDragDisabledByFetch: determineIfDragDisablingFetch({ requestStatus: selectRequestStatus(state) }),
    isProcedureModalMounted: selectSidebarProcedureModalMounted(state),
    timeOffCategory: selectTimeOffFormCategory(state),
    timeOffNonce: selectTimeOffNonce(state),
  }),
  mapActionsToDispatchToProps({
    createIfAllowed,
    setBookedByButton,
    setType,
    feSidebarOpen,
    displayPermissionToast,
  }),
)(withTranslation()(WeekColumn));

const WeekColumnHocMemoized = React.memo(WeekColumnHoc, (prev, next) => {
  const result = equalProps(prev, next);
  if (FLAG.loggerMemoizedColumn) {
    L.debug('WeekColumnHocMemoized()', next?.tag, result, diffShallow(prev, next), { next, prev });
  }
  return result;
});

export default WeekColumnHocMemoized;
